import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { AdminTable } from './AdminTable';
import type { UserWithLocations } from '../services/usersService';

interface UsersTableProps {
  users: UserWithLocations[];
  onEditLocations: (user: UserWithLocations) => void;
  onSendPasswordReset: (user: UserWithLocations) => void;
  onViewNotifications: (user: UserWithLocations) => void;
  isLoading?: boolean;
}

export function UsersTable({
  users,
  onEditLocations,
  onSendPasswordReset,
  onViewNotifications,
  isLoading = false,
}: UsersTableProps) {
  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (user: UserWithLocations) => (
        <div>
          <div className="font-medium">{user.name}</div>
          <div className="text-sm text-gray-500">{user.email}</div>
        </div>
      ),
    },
    {
      key: 'locations',
      header: 'Locations',
      render: (user: UserWithLocations) => (
        <div className="text-sm text-gray-600">
          {user.locations.length > 0
            ? user.locations.map((loc) => loc.short_code).join(', ')
            : 'No locations assigned'}
        </div>
      ),
    },
    {
      key: 'is_admin',
      header: 'Role',
      sortable: true,
      render: (user: UserWithLocations) => (
        <Badge variant={user.is_admin ? 'info' : 'neutral'}>
          {user.is_admin ? 'Admin' : 'User'}
        </Badge>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      sortable: true,
      render: (user: UserWithLocations) => (
        <Badge variant={user.is_active ? 'success' : 'neutral'}>
          {user.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (user: UserWithLocations) => (
        <div className="text-sm text-gray-500">
          {new Date(user.created_at).toLocaleDateString()}
        </div>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (user: UserWithLocations) => (
        <div className="text-sm text-gray-600">
          {user.phone_number || <span className="text-gray-400">—</span>}
        </div>
      ),
    },
    {
      key: 'sms_opted_in',
      header: 'SMS Opt-In',
      render: (user: UserWithLocations) => (
        <Badge variant={user.sms_opted_in_at ? 'success' : 'neutral'}>
          {user.sms_opted_in_at ? 'Opted In' : 'Not Opted In'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (user: UserWithLocations) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onEditLocations(user)}
          >
            Locations
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onViewNotifications(user)}
          >
            Notifications
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onSendPasswordReset(user)}
          >
            Reset PW
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AdminTable
      data={users}
      columns={columns}
      isLoading={isLoading}
      emptyMessage="No users found"
    />
  );
}
